import { Link } from "react-router-dom";
import { Check, Crown, Phone, ShieldCheck, MessageCircle } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { fcfa } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const PERKS = {
  economie: ["Boutique visible dans le catalogue", "Produits illimités", "Assistant IA pour les fiches produits", "Gestion des commandes et livraisons"],
  premium: ["Tout le contenu Économie", "Badge Premium sur la boutique et les produits", "Meilleure visibilité dans les listes", "Mise en avant sur la page d'accueil"],
};

export const PlanCard = ({ plan, action }) => {
  const premium = plan.code === "premium";
  return (
    <div data-testid={`plan-${plan.code}`} className={`relative flex flex-col rounded-3xl p-7 ${premium ? "bg-gray-950 text-white shadow-xl" : "border bg-white"}`}>
      {premium && <span className="absolute -top-3 right-6 flex items-center gap-1 rounded-full bg-amber-400 px-3 py-1 text-xs font-extrabold text-amber-950"><Crown className="h-3 w-3" />Badge Premium</span>}
      <p className="font-display text-2xl font-black">{plan.name}</p>
      <p className="mt-3"><span className="tnum font-display text-4xl font-black">{fcfa(plan.price)}</span><span className={premium ? "text-gray-400" : "text-gray-500"}> / {plan.duration_label}</span></p>
      <ul className="my-6 space-y-2 text-sm">
        {PERKS[plan.code].map((p) => <li key={p} className="flex gap-2"><Check className={`h-4 w-4 shrink-0 ${premium ? "text-amber-400" : "text-green-700"}`} />{p}</li>)}
      </ul>
      <div className="mt-auto">{action}</div>
    </div>
  );
};

export const SubscriptionContact = ({ shopName }) => {
  const { data } = useApi("/subscriptions/contact");
  if (!data) return null;
  const text = `Bonjour Casadeal, je souhaite obtenir les instructions de paiement pour un abonnement vendeur${shopName ? ` (boutique : ${shopName})` : ""}.`;
  const wa = `https://wa.me/${data.phone.replace(/\D/g, "")}?text=${encodeURIComponent(text)}`;
  return (
    <div data-testid="subscription-contact" className="space-y-3 rounded-2xl border-2 border-orange-200 bg-orange-50 p-5 text-sm text-gray-800">
      <p className="font-display text-lg font-bold">Comment payer votre abonnement</p>
      <p>Vous pouvez payer votre abonnement Casadeal par Wave ou Orange Money au numéro suivant. Une fois votre boutique validée, les instructions détaillées et la saisie de la référence de transaction se trouvent dans Espace vendeur → Abonnement.</p>
      <div className="flex flex-wrap gap-2">
      <a href={`tel:${data.phone.replace(/\s/g, "")}`} data-testid="subscription-contact-phone" className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-4 py-2 font-display text-lg font-black text-white transition-colors hover:bg-orange-600">
        <Phone className="h-4 w-4" />{data.phone}
      </a>
      <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="subscription-contact-whatsapp" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 font-display text-lg font-black text-white transition-colors hover:bg-[#1EBE5A]">
        <MessageCircle className="h-4 w-4" />WhatsApp
      </a>
      </div>
      <p>Un bouton WhatsApp est disponible pour faciliter les échanges.</p>
      <p className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-green-800" />Après votre paiement, l'administrateur vérifie manuellement la transaction avant d'activer votre abonnement. Aucun abonnement n'est activé automatiquement sur simple clic ou déclaration.</p>
    </div>
  );
};

export default function Pricing() {
  const { data } = useApi("/plans");
  const { user } = useAuth();
  const target = user?.shop && user.shop.status !== "rejected" ? "/vendeur/abonnement" : "/devenir-vendeur";
  const cta = user?.shop?.status === "approved" ? "Choisir cette formule" : user?.shop && user.shop.status !== "rejected" ? "Voir mon abonnement" : "Ouvrir ma boutique";
  return (
    <div data-testid="pricing-page" className="mx-auto max-w-5xl px-4 py-12 md:px-6">
      <h1 className="font-display text-4xl font-black tracking-tight sm:text-5xl">Abonnements vendeurs</h1>
      <p className="mt-2 max-w-2xl text-gray-600">Économie : 8 000 FCFA pour 1 mois. Premium : 15 000 FCFA pour 2 mois, avec badge Premium et meilleure visibilité. Les abonnements sont activés uniquement après vérification du paiement par l'administrateur Casadeal.</p>
      <ol data-testid="pricing-steps" className="mt-4 flex flex-wrap gap-2 text-xs font-semibold text-gray-700">
        {["1. Ouvrez votre boutique", "2. Validation par Casadeal", "3. Choix de la formule et paiement", "4. Activation par l'administratrice"].map((s) => <li key={s} className="rounded-full bg-gray-100 px-3 py-1">{s}</li>)}
      </ol>
      <div className="mt-10 grid gap-6 md:grid-cols-2">
        {data?.map((p) => <PlanCard key={p.code} plan={p} action={<Button asChild className="w-full rounded-full" variant={p.code === "premium" ? "default" : "outline"} data-testid={`plan-cta-${p.code}`}><Link to={target}>{cta}</Link></Button>} />)}
      </div>
      <div className="mt-8"><SubscriptionContact /></div>
    </div>
  );
}
